import {History, Location} from "history";

export type LocationState = {
    // Für Abwärtskompatibilität
    from?: {
        pathname: string;
    };
    // Neues Konzept: Navigation-Stack
    returnTo?: string; // Finales Ziel (z.B. ursprüngliche Liste)
    immediateReturnTo?: string; // Unmittelbares Ziel (z.B. Kursdetails nach Buchung)
};

/**
 * Standard Navigation-Helper für Zurück-Navigation
 * Unterstützt verschachtelte Navigation mit returnTo-Chain
 *
 * @param useReplace - Wenn true, wird history.replace() statt push() verwendet (für Stack-Bereinigung)
 */
export const returnBackHelper = ({
    history,
    location,
    fallbackUrl,
    onClose,
    useReplace = false,
}: {
    history: History;
    location: Location;
    fallbackUrl: string;
    onClose?: () => void;
    useReplace?: boolean;
}) => {
    if (onClose) {
        return onClose();
    }

    const state = location.state as LocationState | undefined;
    const navigateFn = useReplace ? (url: string, navState?: LocationState) => history.replace(url, navState) : (url: string, navState?: LocationState) => history.push(url, navState);

    // Priorität 1: immediateReturnTo (wenn von Unter-Seite zurück)
    if (state?.immediateReturnTo) {
        return navigateFn(state.immediateReturnTo, {
            returnTo: state.returnTo,
        });
    }

    // Priorität 2: returnTo (finales Ziel)
    if (state?.returnTo) {
        return navigateFn(state.returnTo);
    }

    // Priorität 3: Alte from-Struktur (Abwärtskompatibilität)
    if (state?.from?.pathname) {
        return navigateFn(state.from.pathname);
    }

    // Fallback: Definierte Standard-URL
    return navigateFn(fallbackUrl);
};

/**
 * Spezielle Navigation für CREATE-Actions
 * Entfernt die CREATE-Seite aus dem History Stack durch Verwendung von replace()
 *
 * Use-Case: Nach Buchungserstellung soll CREATE-Seite nicht im Browser-History bleiben
 */
export const navigateAfterCreate = ({history, location, fallbackUrl}: {history: History; location: Location; fallbackUrl: string}) => {
    const state = location.state as LocationState | undefined;

    // Ziel-URL bestimmen (gleiche Logik wie returnBackHelper)
    const targetUrl = state?.immediateReturnTo ?? state?.returnTo ?? state?.from?.pathname ?? fallbackUrl;

    // WICHTIG: replace() statt push() → CREATE-Seite verschwindet aus History
    // Wenn immediateReturnTo existiert, bewahre returnTo für weitere Navigation
    if (state?.immediateReturnTo && state?.returnTo) {
        return history.replace(targetUrl, {
            returnTo: state.returnTo,
        });
    }

    // Sonst einfach zur Ziel-URL navigieren
    return history.replace(targetUrl);
};

/**
 * Navigation-Helper MIT returnTo-Preservation
 * Nützlich für verschachtelte Navigation-Flows
 */
export const navigateWithReturn = ({history, location, targetUrl, preserveReturnTo = true}: {history: History; location: Location; targetUrl: string; preserveReturnTo?: boolean}) => {
    const state = location.state as LocationState | undefined;

    if (preserveReturnTo && state?.returnTo) {
        // Bewahre bestehende returnTo-Chain
        return history.push(targetUrl, {
            returnTo: state.returnTo,
            from: {pathname: location.pathname},
        });
    }

    // Standard-Navigation mit from-Tracking
    return history.push(targetUrl, {
        from: {pathname: location.pathname},
    });
};
